import { useRef, useEffect, useState, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import type { POI, ScheduledPOI } from '@poi-planner/shared';
import { formatTimeBudget } from '../utils/format';
import { useTripStore } from '../store/tripStore';
import { useUIStore } from '../store/uiStore';
import { useLibraryStore } from '../store/libraryStore';
import type { ConstraintViolation } from '../utils/constraints';
import { TagIcon } from './TagIcon';
import { Modal } from './Modal';

// Check if POI has valid coordinates for targeting
function hasCoordinates(poi: POI): boolean {
  return poi.lat != null && poi.lng != null;
}

interface SortablePOICardProps {
  id: string;
  poi: POI;
  scheduledPOI: ScheduledPOI;
  dayId: string;
  computedStartTime?: string;
  violations?: ConstraintViolation[];
  /** Optional numbered badge shown on day-focus cards */
  orderBadge?: number;
  /** Callback when POI is selected (for map focus) */
  onSelectPoi?: (poiId: string | null) => void;
}

function formatStartTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
}

export function SortablePOICard({
  id,
  poi,
  scheduledPOI,
  dayId,
  computedStartTime,
  violations = [],
  orderBadge,
  onSelectPoi,
}: SortablePOICardProps) {
  const unschedulePOI = useTripStore((s) => s.unschedulePOI);
  const unpinFromTrip = useTripStore((s) => s.unpinFromTrip);
  const pinnedPoiIds = useTripStore((s) => s.pinnedPoiIds);
  const setStartTime = useTripStore((s) => s.setStartTime);
  const openPOIEditor = useUIStore((s) => s.openPOIEditor);
  const inlineTimeEdit = useUIStore((s) => s.inlineTimeEdit);
  const setInlineTimeEdit = useUIStore((s) => s.setInlineTimeEdit);
  const targetPoiId = useUIStore((s) => s.targetPoiId);
  const setTargetPoiId = useUIStore((s) => s.setTargetPoiId);
  const tags = useLibraryStore((s) => s.tags);
  const [showUnpinConfirm, setShowUnpinConfirm] = useState(false);
  const isPinned = pinnedPoiIds.has(poi.id);

  const isTargeted = targetPoiId === poi.id;
  const canTarget = hasCoordinates(poi);

  const markerTag = useMemo(() => {
    if (poi.primaryTagId) {
      const primary = tags.find((tag) => tag.id === poi.primaryTagId);
      if (primary) return primary;
    }
    if (poi.tags?.length) {
      const fallback = tags.find((tag) => tag.name === poi.tags[0]);
      if (fallback) return fallback;
    }
    return null;
  }, [tags, poi.primaryTagId, poi.tags]);

  const markerIcon = markerTag?.icon ?? 'phosphor:map-pin';
  const markerColor = markerTag?.color ?? '#111418';

  const wasDragging = useRef(false);
  const [timeValue, setTimeValue] = useState(scheduledPOI.startTime ?? '');

  const isEditingTime = inlineTimeEdit?.dayId === dayId && inlineTimeEdit?.poiId === poi.id;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: { type: 'scheduled-poi', poiId: poi.id, dayId },
  });

  // Track drag state
  useEffect(() => {
    if (isDragging) wasDragging.current = true;
  }, [isDragging]);

  // Sync time value when scheduledPOI changes
  useEffect(() => {
    setTimeValue(scheduledPOI.startTime ?? '');
  }, [scheduledPOI.startTime]);

  const handleCardClick = () => {
    if (wasDragging.current) {
      wasDragging.current = false;
      return;
    }
    // If we have onSelectPoi callback and POI has coordinates, select it for map focus
    if (onSelectPoi && canTarget) {
      onSelectPoi(poi.id);
    }
  };

  const handleTimeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (wasDragging.current) {
      wasDragging.current = false;
      return;
    }
    setTimeValue(scheduledPOI.startTime ?? '');
    setInlineTimeEdit({ dayId, poiId: poi.id });
  };

  const handleTimeCommit = () => {
    if (!isEditingTime) return;
    setStartTime(dayId, poi.id, timeValue || null);
    setInlineTimeEdit(null);
  };

  const handleTimeKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') handleTimeCommit();
    if (e.key === 'Escape') setInlineTimeEdit(null);
  };

  const hasWarnings = violations.length > 0;
  const [showTooltip, setShowTooltip] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      data-poi-card={id}
      style={style}
      className={clsx(
        'group flex flex-col gap-2 p-3 rounded-xl cursor-pointer touch-none relative transition-all',
        isDragging && 'opacity-40 shadow-lg',
        isTargeted
          ? 'bg-blue-50 border-2 border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.15),0_4px_12px_rgba(59,130,246,0.12)]'
          : hasWarnings
            ? 'bg-white border-2 border-amber-500 shadow-[0_1px_4px_rgba(245,158,11,0.15)]'
            : 'bg-white border border-border shadow-sm hover:shadow-md'
      )}
      {...attributes}
      {...listeners}
      onClick={handleCardClick}
    >
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-2">
          {orderBadge != null && (
            <span className="absolute -top-2 -right-2 h-5 min-w-5 px-1.5 rounded-full bg-blue-500 border-2 border-white text-[11px] font-extrabold text-white leading-none inline-flex items-center justify-center shadow-sm pointer-events-none z-10">
              {orderBadge}
            </span>
          )}
          {isEditingTime ? (
            <input
              type="time"
              value={timeValue}
              onChange={(e) => setTimeValue(e.target.value)}
              onBlur={handleTimeCommit}
              onKeyDown={handleTimeKeyDown}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              className="text-[11px] font-bold font-sans bg-white border-2 border-text-primary rounded px-1 py-0.5 outline-none w-[90px]"
            />
          ) : scheduledPOI.startTime ? (
            <span
              onClick={handleTimeClick}
              className="time-badge"
            >
              {formatStartTime(scheduledPOI.startTime)}
            </span>
          ) : computedStartTime ? (
            <span
              onClick={handleTimeClick}
              className="time-badge italic text-text-primary/35"
            >
              {formatStartTime(computedStartTime)}
            </span>
          ) : (
            <span
              onClick={handleTimeClick}
              className="time-badge text-text-primary/30"
            >
              —
            </span>
          )}
          <span className="text-sm font-bold text-text-primary">
            {poi.name}
          </span>
        </div>
        <div className="flex items-center gap-0.5">
          {canTarget && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setTargetPoiId(isTargeted ? null : poi.id);
              }}
              title={isTargeted ? 'Clear proximity target' : 'Sort sidebar by distance from this POI'}
              className={clsx(
                'flex items-center justify-center size-[22px] border-none rounded cursor-pointer p-0 transition-all',
                isTargeted
                  ? 'bg-blue-500 text-white opacity-100'
                  : 'bg-transparent text-text-secondary opacity-0 group-hover:opacity-100 hover:bg-blue-500/10 hover:text-blue-500'
              )}
            >
              <span className="material-symbols-outlined text-base">gps_fixed</span>
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              openPOIEditor('edit', poi.id);
            }}
            title="Edit POI"
            className="flex items-center justify-center size-[22px] border-none rounded cursor-pointer p-0 bg-transparent text-text-secondary opacity-0 group-hover:opacity-100 transition-all hover:bg-slate-100"
          >
            <span className="material-symbols-outlined text-base">edit</span>
          </button>
          {isPinned && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowUnpinConfirm(true);
              }}
              title="Remove from trip (unschedule and unpin)"
              className="flex items-center justify-center size-[22px] border-none rounded cursor-pointer p-0 bg-transparent text-purple-500 opacity-0 group-hover:opacity-100 transition-all hover:bg-purple-500/10"
            >
              <span className="material-symbols-outlined text-base">keep_off</span>
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              unschedulePOI(poi.id, dayId);
            }}
            className="bg-transparent border-none cursor-pointer text-text-secondary p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:text-danger"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>
      </div>
      <Modal
        open={showUnpinConfirm}
        title="Remove from trip?"
        icon="keep_off"
        onClose={() => setShowUnpinConfirm(false)}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-text-secondary">
            This will unschedule <strong>{poi.name}</strong> and remove it from the trip entirely.
          </p>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowUnpinConfirm(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowUnpinConfirm(false);
                unpinFromTrip(poi.id, dayId);
              }}
              className="btn-danger"
            >
              Remove from trip
            </button>
          </div>
        </div>
      </Modal>
      <div className="flex items-center gap-1.5 text-text-secondary text-xs">
        <span className="material-symbols-outlined text-base">schedule</span>
        <span>Budget: {formatTimeBudget(poi.timeBudget)}</span>
      </div>

      {hasWarnings && (
        <div
          className="relative inline-flex"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="warning-badge">
            <span className="material-symbols-outlined text-sm">warning</span>
            <span>{violations.length} warning{violations.length > 1 ? 's' : ''}</span>
          </div>
          {showTooltip && (
            <div className="absolute bottom-full left-0 mb-1.5 bg-slate-800 text-slate-50 rounded-lg px-3 py-2 text-[11px] leading-relaxed whitespace-nowrap z-50 shadow-lg pointer-events-none">
              {violations.map((v, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-xs text-amber-400">
                    {v.type === 'availability' ? 'event_busy' : v.type === 'idealDay' ? 'calendar_today' : 'schedule'}
                  </span>
                  <span>{v.message}</span>
                </div>
              ))}
              <div className="absolute -bottom-1 left-4 size-2 bg-slate-800 rotate-45" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
